/**
 * React Query hooks for project operations.
 * Provides declarative data fetching with caching and automatic refetching.
 *
 * @module hooks/useProjects
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { projectService } from '@/services';
import type { ProjectRequest } from '@/types';

/**
 * Query key factory for projects.
 * Ensures consistent cache key generation.
 */
export const projectKeys = {
  all: ['projects'] as const,
  lists: () => [...projectKeys.all, 'list'] as const,
  list: () => [...projectKeys.lists()] as const,
};

/**
 * Hook to fetch all projects for the current user.
 *
 * @example
 * const { data, isLoading, error } = useProjects();
 */
export function useProjects() {
  return useQuery({
    queryKey: projectKeys.list(),
    queryFn: () => projectService.list(),
  });
}

/**
 * Hook to create a new project (provisions GitHub repo + stores metadata).
 *
 * @example
 * const mutation = useCreateProject();
 * mutation.mutate({ name: 'My Project', repoName: 'my-project-repo' });
 */
export function useCreateProject() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (request: ProjectRequest) => projectService.create(request),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: projectKeys.all });
    },
  });
}

/**
 * Hook to delete a project.
 *
 * @example
 * const mutation = useDeleteProject();
 * mutation.mutate('project-id');
 */
export function useDeleteProject() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => projectService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: projectKeys.all });
    },
  });
}
